/**
 * Support widget — the tracking vocabulary (#5619).
 *
 * The Tracking Events Dictionary is one vocabulary for every support surface, but it
 * was written down twice: as TypeScript enums in fe-react and as bare string literals
 * in the Django adapter. Two surfaces reporting `surface`/`channel` values that differ
 * by a character produce two funnels that silently never join, and nothing fails.
 *
 * Const objects rather than `enum`s so the Django adapter — which has no PostHog
 * types of its own — can spread them straight into a property bag, and so each name
 * doubles as its own union type.
 */

/** Where the support entry point lives. One value per surface, never reused. */
export const SupportSurface = {
  BSDIR_PAGE: "bsdir_page",
  EXIT_INTENT_JOB_FORM: "exit_intent_job_form"
} as const
export type SupportSurface = (typeof SupportSurface)[keyof typeof SupportSurface]

/** What made the entry point appear. */
export const SupportTrigger = {
  TIMER: "timer",
  IMMEDIATE: "immediate",
  /** The customer pressed the job form's close control. */
  EXIT_CLOSE: "exit_close",
  /** Reserved: desktop pointer leaving the viewport. Not wired yet. */
  EXIT_MOUSEOUT: "exit_mouseout"
} as const
export type SupportTrigger = (typeof SupportTrigger)[keyof typeof SupportTrigger]

/** The channel the customer picked. Mirrors the endpoint's `channels` keys. */
export const SupportChannel = {
  CHAT: "chat",
  CALL: "call",
  CONTACT_FORM: "contact_form"
} as const
export type SupportChannel = (typeof SupportChannel)[keyof typeof SupportChannel]

/**
 * How a surface was closed without a channel being picked.
 *
 * `DismissMethod` in `types.ts` is an alias of this, so the core's `closeSheet(method)`
 * and the dictionary cannot drift.
 */
export const SupportDismissMethod = {
  CLOSE_X: "close_x",
  BACKDROP: "backdrop",
  ESC: "esc",
  AUTO_HIDE: "auto_hide",
  /** Exit intent: the customer chose to carry on with the job form. */
  CONTINUE_JOB: "continue_job",
  /** Exit intent: the customer abandoned the job form. */
  CLOSE_FORM: "close_form"
} as const
export type SupportDismissMethod = (typeof SupportDismissMethod)[keyof typeof SupportDismissMethod]

/**
 * The core's own internal event names → the dictionary's `channel` value. The core
 * stays framework-agnostic and emits one event per channel; every adapter has to make
 * the same mapping, so it lives here rather than in each of them.
 */
export const SUPPORT_CHANNEL_BY_CORE_EVENT: { [coreEvent: string]: SupportChannel } = {
  support_widget_chat: SupportChannel.CHAT,
  support_widget_phone: SupportChannel.CALL,
  support_widget_form: SupportChannel.CONTACT_FORM
}
